<template>
  <div class="item-selector-step">
    <!-- Loading state -->
    <div
      v-if="loading"
      class="loading-container"
    >
      <i class="fas fa-spinner fa-spin fa-2x" />
      <span>{{ t('import.itemSelector.loading') }}</span>
    </div>

    <!-- Error state -->
    <div
      v-else-if="error"
      class="error-container"
    >
      <div class="error-icon">
        <i class="fas fa-exclamation-triangle" />
      </div>
      <div class="error-content">
        <h3 class="error-title">
          {{ t('import.itemSelector.errorTitle') }}
        </h3>
        <p class="error-message">
          {{ t(error) }}
        </p>
      </div>
    </div>

    <!-- Items list -->
    <div
      v-else
      class="items-container"
    >
      <div class="selector-header">
        <h2 class="selector-title">
          {{ t('import.itemSelector.title') }}
        </h2>
        <p class="selector-description">
          {{ t('import.itemSelector.description') }}
        </p>
      </div>

      <!-- Selection summary bar -->
      <div class="selection-bar">
        <div class="selection-bar__left">
          <Checkbox
            v-model="selectAll"
            :binary="true"
            input-id="select-all"
            @change="onSelectAllChange"
          />
          <label
            for="select-all"
            class="selection-bar__label"
          >
            {{ t('import.itemSelector.selectAll') }}
          </label>
        </div>
        <div class="selection-bar__right">
          <span class="selection-bar__badge">
            {{ selectedCount }} / {{ items.length }}
          </span>
          <span class="selection-bar__text">{{ t('import.itemSelector.selected') }}</span>
        </div>
      </div>

      <!-- Items cards grid -->
      <div class="items-grid">
        <div
          v-for="item in items"
          :key="item.id"
          class="item-card"
          :class="{ 'item-card--selected': item.selected }"
          @click="toggleItem(item)"
        >
          <!-- Checkbox overlay -->
          <div class="item-card__checkbox">
            <Checkbox
              v-model="item.selected"
              :binary="true"
              :input-id="`item-${item.id}`"
              @click.stop
            />
          </div>

          <!-- Preview area -->
          <div class="item-card__preview">
            <!-- Image preview: auto-scroll on hover via background-position transition -->
            <div
              v-if="item.previews && item.previews.length > 0"
              class="preview-scroll"
              :style="{ backgroundImage: `url(${item.previews[0]})` }"
            />

            <!-- Placeholder when no preview -->
            <div
              v-else
              class="preview-placeholder"
            >
              <div class="preview-placeholder__icon">
                <i :class="getItemIcon()" />
              </div>
              <span class="preview-placeholder__label">{{ t('import.itemSelector.noPreview') }}</span>
            </div>

            <!-- HTML preview icon (only when HTML previews available) -->
            <button
              v-if="item.htmlPreviewCount && item.htmlPreviewCount > 0"
              class="preview-html-btn"
              :title="t('import.itemSelector.htmlPreviewTitle')"
              @click.stop="openHtmlPreview(item)"
            >
              <i class="fa-light fa-eye" />
            </button>
          </div>

          <!-- Card body -->
          <div class="item-card__body">
            <div class="item-card__info">
              <div class="item-card__name">
                {{ item.name }}
              </div>
              <div class="item-card__meta">
                <i :class="getItemIcon()" />
                <span>{{ getItemTypeLabel() }}</span>
              </div>
            </div>
            <div
              v-if="item.selected"
              class="item-card__status"
            >
              <i class="fas fa-check-circle" />
            </div>
          </div>
        </div>
      </div>

      <!-- Validation message -->
      <div
        v-if="!isValid"
        class="validation-message"
      >
        <i class="fas fa-info-circle" />
        <span>{{ t('import.itemSelector.selectAtLeastOne') }}</span>
      </div>
    </div>

    <!-- HTML Preview Slider -->
    <TemplateHtmlPreviewSlider
      v-if="htmlSliderItem && htmlSliderItem.htmlPreviewCount && htmlSliderItem.htmlPreviewSessionId"
      :session-id="htmlSliderItem.htmlPreviewSessionId"
      :uid="htmlSliderItem.id"
      :preview-count="htmlSliderItem.htmlPreviewCount"
      :hide-media-toggle="importType === 'campaign_automation' || importType === 'campaign_bulk'"
      @close="closeHtmlPreview"
    />
  </div>
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, PropType, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Checkbox from 'primevue/checkbox';
import TemplateHtmlPreviewSlider from '@/components/import/TemplateHtmlPreviewSlider.vue';
import type { SelectableItem } from '@/composables/import/types';

export default defineComponent({
  name: 'ItemSelectorStep',

  components: {
    Checkbox,
    TemplateHtmlPreviewSlider,
  },

  props: {
    /** Items to display for selection */
    modelValue: {
      type: Array as PropType<SelectableItem[]>,
      required: true,
    },

    /** Import type (campaign_automation, campaign_bulk, template) */
    importType: {
      type: String,
      required: true,
    },

    /** Loading state */
    loading: {
      type: Boolean,
      default: false,
    },

    /** Error message */
    error: {
      type: String,
      default: '',
    },
  },

  emits: ['update:modelValue', 'valid-change'],

  setup(props, { emit }) {
    const { t } = useI18n();

    // Flag to prevent watch from re-triggering after our own emit
    let isInternalUpdate = false;

    // Local copy of items
    const items = ref<SelectableItem[]>([]);

    // Select all state
    const selectAll = ref(true);

    // HTML preview slider state
    const htmlSliderItem = ref<SelectableItem | null>(null);

    const openHtmlPreview = (item: SelectableItem) => {
      htmlSliderItem.value = item;
    };

    const closeHtmlPreview = () => {
      htmlSliderItem.value = null;
    };

    // Count of selected items
    const selectedCount = computed(() => items.value.filter((item) => item.selected).length);

    // Validation: at least one item must be selected
    const isValid = computed(() => selectedCount.value > 0);

    // Get icon based on import type
    const getItemIcon = (): string => {
      switch (props.importType) {
        case 'campaign_automation':
          return 'fa-light fa-robot';
        case 'campaign_bulk':
          return 'fa-light fa-paper-plane';
        case 'template':
          return 'fa-light fa-palette';
        default:
          return 'fa-light fa-file';
      }
    };

    // Get human-readable label for import type
    const getItemTypeLabel = (): string => {
      switch (props.importType) {
        case 'campaign_automation':
          return t('import.itemSelector.types.automation');
        case 'campaign_bulk':
          return t('import.itemSelector.types.bulk');
        case 'template':
          return t('import.itemSelector.types.template');
        default:
          return t('import.itemSelector.types.item');
      }
    };

    // Update select all checkbox state based on items
    const updateSelectAllState = () => {
      selectAll.value = items.value.length > 0 && items.value.every((item) => item.selected);
    };

    // Emit changes to parent
    const emitChanges = () => {
      isInternalUpdate = true;
      const updatedItems: SelectableItem[] = items.value.map((item) => ({
        id: item.id,
        name: item.name,
        selected: item.selected,
        preview: item.preview,
        previews: item.previews,
        itemType: item.itemType,
      }));
      emit('update:modelValue', updatedItems);
      emit('valid-change', isValid.value);
      setTimeout(() => {
        isInternalUpdate = false;
      }, 0);
    };

    // Toggle item selection
    const toggleItem = (item: SelectableItem) => {
      const index = items.value.findIndex((i) => i.id === item.id);
      if (index !== -1) {
        items.value[index].selected = !items.value[index].selected;
        updateSelectAllState();
        emitChanges();
      }
    };

    // Handle select all change
    const onSelectAllChange = () => {
      items.value.forEach((i) => {
        // eslint-disable-next-line no-param-reassign
        i.selected = selectAll.value;
      });
      emitChanges();
    };

    // Sync with parent modelValue
    watch(
      () => props.modelValue,
      (newVal, oldVal) => {
        if (isInternalUpdate) {
          return;
        }

        if (!oldVal || items.value.length === 0) {
          items.value = newVal.map((item) => ({ ...item }));
          updateSelectAllState();
          return;
        }

        const currentIds = items.value.map((i) => i.id).sort().join(',');
        const newIds = newVal.map((i) => i.id).sort().join(',');

        if (currentIds !== newIds) {
          items.value = newVal.map((item) => ({ ...item }));
          updateSelectAllState();
        }
      },
      { immediate: true, deep: true },
    );

    // Emit validity on mount
    watch(isValid, (newVal) => {
      emit('valid-change', newVal);
    }, { immediate: true });

    return {
      t,
      items,
      selectAll,
      selectedCount,
      isValid,
      getItemIcon,
      getItemTypeLabel,
      toggleItem,
      onSelectAllChange,
      htmlSliderItem,
      openHtmlPreview,
      closeHtmlPreview,
    };
  },
});
</script>

<style scoped lang="scss">
.item-selector-step {
  padding: 1.5rem;
  max-width: 960px;
  margin: 0 auto;
}

// ==========================================
// Loading & Error states
// ==========================================
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  gap: 1rem;
  color: #64748b;
}

.error-container {
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 12px;
  margin: 2rem 0;
}

.error-icon {
  font-size: 1.5rem;
  color: #ef4444;
}

.error-title {
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #991b1b;
}

.error-message {
  margin: 0;
  color: #dc2626;
}

// ==========================================
// Header
// ==========================================
.selector-header {
  text-align: center;
  margin-bottom: 1.75rem;
}

.selector-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.375rem 0;
}

.selector-description {
  color: #64748b;
  margin: 0;
  font-size: 0.9375rem;
}

// ==========================================
// Selection bar
// ==========================================
.selection-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  margin-bottom: 1.25rem;

  &__left {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__label {
    font-weight: 500;
    font-size: 0.875rem;
    cursor: pointer;
    color: #334155;
  }

  &__right {
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 2.5rem;
    padding: 0.125rem 0.5rem;
    background: #94c748;
    color: white;
    border-radius: 20px;
    font-size: 0.8125rem;
    font-weight: 600;
  }

  &__text {
    font-size: 0.8125rem;
    color: #64748b;
  }
}

// ==========================================
// Items grid
// ==========================================
.items-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
}

// ==========================================
// Item card
// ==========================================
.item-card {
  position: relative;
  background: #ffffff;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    border-color: #94c748;
    box-shadow: 0 4px 16px rgba(148, 199, 72, 0.15);
    transform: translateY(-1px);
  }

  &--selected {
    border-color: #94c748;
    background: #f7fdf0;

    .item-card__checkbox {
      opacity: 1;
    }
  }

  &__checkbox {
    position: absolute;
    top: 0.625rem;
    left: 0.625rem;
    z-index: 2;
    opacity: 0.85;
    transition: opacity 0.2s ease;

    .item-card:hover & {
      opacity: 1;
    }
  }

  // Preview area
  &__preview {
    position: relative;
    width: 100%;
    height: 180px;
    background: #f8fafc;
    overflow: hidden;
  }

  // Card body
  &__body {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.875rem 1rem;
    border-top: 1px solid #f1f5f9;
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-weight: 600;
    font-size: 0.9375rem;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.3;
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    margin-top: 0.125rem;
    font-size: 0.75rem;
    color: #94a3b8;

    i {
      font-size: 0.6875rem;
    }
  }

  &__status {
    flex-shrink: 0;
    color: #94c748;
    font-size: 1.125rem;
  }
}

// ==========================================
// Preview scroll (auto-scroll on hover via background-position)
// ==========================================
.preview-scroll {
  width: 100%;
  height: 100%;
  background-size: 100% auto;
  background-position: center top;
  background-repeat: no-repeat;
  transition: background-position 3s ease-in-out;

  .item-card:hover & {
    background-position: center bottom;
  }
}

// ==========================================
// HTML preview button (eye icon overlay)
// ==========================================
.preview-html-btn {
  position: absolute;
  bottom: 0.5rem;
  right: 0.5rem;
  z-index: 3;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.55);
  color: #ffffff;
  font-size: 0.875rem;
  cursor: pointer;
  backdrop-filter: blur(4px);
  transition: all 0.2s ease;

  &:hover {
    background: #94c748;
    transform: scale(1.1);
  }
}

// ==========================================
// Preview placeholder
// ==========================================
.preview-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  gap: 0.5rem;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);

  &__icon {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 12px;
    color: #94a3b8;
    font-size: 1.25rem;
  }

  &__label {
    font-size: 0.75rem;
    color: #94a3b8;
    font-weight: 500;
  }
}

// ==========================================
// Validation message
// ==========================================
.validation-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background-color: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 10px;
  margin-top: 1.25rem;
  color: #c2410c;
  font-size: 0.875rem;
}
</style>
